import { LabeledInfo } from "./LabeledInfo";
import {
  AccessTimeOutlined,
  AssignmentOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  ErrorOutline,
  ExpandMore,
  PlayArrowOutlined,
  SearchOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  PutawayRequest,
  PutawaySimulation,
  PutawayTask,
  PutawayTour,
  PutawayQueueCandidate,
  PutawayEquipmentSelection,
  PutawayAlert as PutawayAlertData,
  ProcessTransferTask,
  RepackagingTask,
  acceptPutaway,
  cancelPutawayTour,
  claimPutawayTask,
  completePutawayTourLoading,
  createPutawayTour,
  claimProcessTransfer,
  completeProcessTransfer,
  identifyPutawayPackaging,
  loadProcessPointMasterData,
  loadPackaging,
  loadGeneratedPutawayDocumentContent,
  loadPutawayAlerts,
  loadPutawayDecision,
  loadPutawayRequests,
  loadPutawayQueueCandidates,
  loadPutawayTaskHandlingEquipment,
  resimulatePutawayAlert,
  loadPutawayTourHandlingEquipment,
  loadPutawayTasks,
  loadPutawayTours,
  loadPutawayWorkQueues,
  loadProcessTransfers,
  loadRepackagingTasks,
  reportProcessTransferException,
  resolveProcessTransferException,
  scanPutawayTourEquipment,
  scanPutawayTourHu,
  simulatePutaway
} from "./api";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { SourceTrace } from "./SourceTrace";
import { ScanInput } from "./ScanInput";
import { PutawayTaskDialog } from "./PutawayTaskDialog";
import { ListRowActions } from "./ListRowActions";
import { compareOptionalDates, matchesListSyntax } from "./listSearch";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { setDiagnosticContext } from "./terminalDiagnostics";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

function reasonLabel(t: TFunction, reason: string) {
  return t(`putaway.reasons.${reason}`, { defaultValue: reason });
}

function resolutionLabel(t: TFunction, action: string) {
  return t(`putaway.resolutions.${action}`, { defaultValue: action });
}

function transferLabel(task: ProcessTransferTask, t: TFunction) {
  if (task.transferType === "PUTAWAY_TO_QUALITY") {
    return t("putaway.transfers.PUTAWAY_TO_QUALITY");
  }
  if (task.transferType === "PICKING_TO_OUTBOUND_REPACKAGING") {
    return t("putaway.transfers.PICKING_TO_OUTBOUND_REPACKAGING");
  }
  if (task.transferType === "STOCK_TO_QUARANTINE") {
    return t("putaway.transfers.STOCK_TO_QUARANTINE");
  }
  return t("putaway.transfers.QUALITY_TO_REPACKAGING");
}

async function openPutawayMissionDocument(task: PutawayTask) {
  if (!task.missionDocumentId) return;
  const preview = window.open("", "_blank");
  try {
    const blob = await loadGeneratedPutawayDocumentContent(
      task.id, task.missionDocumentId
    );
    const url = URL.createObjectURL(blob);
    if (preview) preview.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (cause) {
    preview?.close();
    throw cause;
  }
}

function PutawayMissionDocumentLink({ task }: { task: PutawayTask }) {
  const { t } = useTranslation();
  const [openError, setOpenError] = useState<string | null>(null);

  if (!task.missionDocumentId) {
    return null;
  }

  const openDocument = async (documentId: string) => {
    setOpenError(null);
    try {
      await openPutawayMissionDocument({ ...task, missionDocumentId: documentId });
    } catch (cause) {
      setOpenError(cause instanceof Error
        ? cause.message : t("putaway.errors.documentOpen"));
    }
  };

  return (
    <Stack spacing={0.5} alignItems={{ md: "flex-end" }}>
      <Button size="small" variant="outlined" startIcon={<DescriptionOutlined />}
        title={task.missionDocumentFilename ?? undefined}
        data-testid={`putaway-mission-document-${task.huNumber}`}
        onClick={() => void openDocument(task.missionDocumentId!)}>
        {t("putaway.labels.missionDocument")}
      </Button>
      {openError && (
        <Typography variant="caption" color="error">{openError}</Typography>
      )}
    </Stack>
  );
}

type ActivityTab = "requests" | "queues" | "tours" | "tasks" | "alerts" | "closed";

export function PutawayPage({
  onBack, onOpenRepackaging, initialTab = "queues", initialTaskId = null,
  initialOpenDetail = false
}: {
  onBack: () => void;
  onOpenRepackaging: (taskId: string) => void;
  initialTab?: ActivityTab;
  initialTaskId?: string | null;
  initialOpenDetail?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const workQueueLabel = (code: string | null | undefined,
    fallback: string | null | undefined) => code === "HEAVY" || code === "PEDESTRIAN"
    ? t(`putaway.queueNames.${code}`)
    : fallback ?? "";
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const { configuration: measurements } = useMeasurementSettings();
  const weightLabel = (valueKg: number) =>
    formatMeasurement(valueKg, "MASS", measurements, locale);
  const volumeLabel = (valueMm3: number) =>
    formatMeasurement(valueMm3, "VOLUME", measurements, locale);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<PutawayRequest | null>(null);
  const [loadingTour, setLoadingTour] = useState<PutawayTour | null>(null);
  const [tourScan, setTourScan] = useState("");
  const [skippedTourStepIds, setSkippedTourStepIds] = useState<string[]>([]);
  const [executionTourId, setExecutionTourId] = useState<string | null>(null);
  const [result, setResult] = useState<PutawaySimulation | null>(null);
  const [createdTask, setCreatedTask] = useState<PutawayTask | null>(null);
  const [executionTask, setExecutionTask] = useState<PutawayTask | null>(null);
  const [selectedTransfer, setSelectedTransfer] =
    useState<ProcessTransferTask | null>(null);
  const [transferExceptionMode, setTransferExceptionMode] =
    useState<"report" | "resolve" | null>(null);
  const [transferExceptionCode, setTransferExceptionCode] = useState("OTHER");
  const [transferExceptionAction, setTransferExceptionAction] =
    useState("RETRY");
  const [transferExceptionComment, setTransferExceptionComment] = useState("");
  const [transferDestinationId, setTransferDestinationId] = useState("");
  const [packagingAlert, setPackagingAlert] =
    useState<PutawayAlertData | null>(null);
  const [packagingId, setPackagingId] = useState("");
  const [packagingComment, setPackagingComment] = useState("");
  const [resimulationAlert, setResimulationAlert] =
    useState<PutawayAlertData | null>(null);
  const [releaseRejectedBin, setReleaseRejectedBin] = useState(false);
  const [resimulationComment, setResimulationComment] = useState("");
  const [tab, setTab] = useState<ActivityTab>(initialTab);
  const [identifierFilter, setIdentifierFilter] = useState("");
  const [contextFilter, setContextFilter] = useState("");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [expandedQueueId, setExpandedQueueId] = useState<string | null>(null);
  const [expandedTourId, setExpandedTourId] = useState<string | null>(null);
  const [expandedClosedItemId, setExpandedClosedItemId] = useState<string | null>(null);
  const [selectedQueueTaskIds, setSelectedQueueTaskIds] = useState<string[]>([]);
  const [mobileSelection, setMobileSelection] = useState<{
    kind: "request" | "tour" | "task";
    id: string;
  } | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);
  const [directEquipment, setDirectEquipment] = useState<{
    queueId: string;
    candidate: PutawayQueueCandidate;
    selection: PutawayEquipmentSelection;
  } | null>(null);
  const tourEquipment = useQuery({
    queryKey: ["putaway-tour-equipment", loadingTour?.id],
    queryFn: () => loadPutawayTourHandlingEquipment(loadingTour!.id),
    enabled: loadingTour != null && !loadingTour.equipmentScanned
  });
  const [directEquipmentId, setDirectEquipmentId] = useState("");
  const [directEquipmentIdentifier, setDirectEquipmentIdentifier] = useState("");
  const [directNoEquipment, setDirectNoEquipment] = useState(false);
  const requests = useQuery({
    queryKey: ["putaway-requests"],
    queryFn: loadPutawayRequests
  });
  const tasks = useQuery({
    queryKey: ["putaway-tasks"],
    queryFn: loadPutawayTasks,
    refetchInterval: tab === "tours" ? 5_000 : false
  });
  const workQueues = useQuery({
    queryKey: ["putaway-work-queues"],
    queryFn: loadPutawayWorkQueues
  });
  const queueCandidates = useQuery({
    queryKey: ["putaway-queue-candidates", expandedQueueId],
    queryFn: () => loadPutawayQueueCandidates(expandedQueueId!),
    enabled: Boolean(expandedQueueId)
  });
  const tours = useQuery({
    queryKey: ["putaway-tours"],
    queryFn: loadPutawayTours
  });
  const executionTour = (tours.data ?? []).find((tour) =>
    tour.id === executionTourId) ?? null;
  const createTour = useMutation({
    mutationFn: ({ queueId, taskIds, direct = false, handlingEquipmentId,
      scanEquipment = false, handlingEquipmentIdentifier }: {
      queueId: string; taskIds: string[]; direct?: boolean;
      handlingEquipmentId?: string | null; scanEquipment?: boolean;
      handlingEquipmentIdentifier?: string | null
    }) => createPutawayTour(
      queueId, taskIds, direct, handlingEquipmentId ?? null, scanEquipment,
      handlingEquipmentIdentifier ?? null
    ),
    onSuccess: async (created) => {
      if (created.status === "LOADING") {
        setSkippedTourStepIds([]);
        setLoadingTour(created);
      }
      else setTab("tours");
      setDirectEquipment(null);
      setDirectEquipmentId("");
      setDirectEquipmentIdentifier("");
      setDirectNoEquipment(false);
      await queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
    }
  });
  const startDirectMission = async (
    queueId: string, candidate: PutawayQueueCandidate
  ) => {
    const selection = await queryClient.fetchQuery({
      queryKey: ["putaway-task-equipment", candidate.taskId],
      queryFn: () => loadPutawayTaskHandlingEquipment(candidate.taskId)
    });
    setDirectEquipment({ queueId, candidate, selection });
    setDirectEquipmentId("");
    setDirectEquipmentIdentifier("");
    setDirectNoEquipment(false);
  };
  const scanTour = useMutation({
    mutationFn: async () => {
      if (!loadingTour || !tourScan.trim()) throw new Error(t("putaway.errors.scanRequired"));
      return loadingTour.equipmentScanned
        ? scanPutawayTourHu(loadingTour, tourScan.trim())
        : scanPutawayTourEquipment(loadingTour, tourScan.trim());
    },
    onSuccess: async (updated) => {
      setTourScan("");
      setLoadingTour(updated.status === "LOADING" ? updated : null);
      if (updated.status === "READY") setTab("tours");
      await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    }
  });
  const completeTourLoading = useMutation({
    mutationFn: async () => {
      if (!loadingTour) throw new Error(t("putaway.errors.loadingMissing"));
      return completePutawayTourLoading(loadingTour);
    },
    onSuccess: async () => {
      setLoadingTour(null);
      setSkippedTourStepIds([]);
      setTab("tours");
      await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-queue-candidates"] });
    }
  });
  const cancelTour = useMutation({
    mutationFn: cancelPutawayTour,
    onSuccess: async () => {
      setLoadingTour(null);
      setSkippedTourStepIds([]);
      setExecutionTourId(null);
      await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-queue-candidates"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    }
  });
  const startTourStep = useMutation({
    mutationFn: async (task: PutawayTask) =>
      task.status === "CREATED" ? claimPutawayTask(task) : task,
    onSuccess: async (task) => {
      setExecutionTask(task);
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
    }
  });
  useEffect(() => {
    if (!expandedQueueId || !queueCandidates.data) return;
    const queue = workQueues.data?.find((entry) => entry.id === expandedQueueId);
    if (!queue) return;
    let weight = 0;
    let volume = 0;
    const selected: string[] = [];
    for (const candidate of queueCandidates.data) {
      if (selected.length >= queue.maxHandlingUnits) break;
      const nextWeight = weight + candidate.weightKg;
      const nextVolume = volume + candidate.volumeMm3;
      if (queue.maxWeightKg != null && nextWeight > queue.maxWeightKg) continue;
      if (queue.maxVolumeMm3 != null && nextVolume > queue.maxVolumeMm3) continue;
      selected.push(candidate.taskId);
      weight = nextWeight;
      volume = nextVolume;
    }
    setSelectedQueueTaskIds(selected);
  }, [expandedQueueId, queueCandidates.data, workQueues.data]);
  const transfers = useQuery({
    queryKey: ["process-transfers"],
    queryFn: loadProcessTransfers
  });
  const repackaging = useQuery({
    queryKey: ["repackaging-tasks"],
    queryFn: loadRepackagingTasks
  });
  const processPoints = useQuery({
    queryKey: ["process-point-master-data"],
    queryFn: loadProcessPointMasterData
  });
  const transferAction = useMutation({
    mutationFn: (task: ProcessTransferTask) => task.status === "OPEN"
      ? claimProcessTransfer(task) : completeProcessTransfer(task),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["process-transfers"] });
      await queryClient.invalidateQueries({ queryKey: ["repackaging-tasks"] });
    }
  });
  const reportTransferException = useMutation({
    mutationFn: () => reportProcessTransferException(
      selectedTransfer!, transferExceptionCode, transferExceptionComment.trim()
    ),
    onSuccess: async (updated) => {
      setSelectedTransfer(updated);
      setTransferExceptionMode(null);
      setTransferExceptionComment("");
      setTab("alerts");
      await queryClient.invalidateQueries({ queryKey: ["process-transfers"] });
    }
  });
  const resolveTransferException = useMutation({
    mutationFn: () => resolveProcessTransferException(
      selectedTransfer!, transferExceptionAction,
      transferExceptionComment.trim(),
      transferExceptionAction === "CHANGE_DESTINATION"
        ? transferDestinationId : null
    ),
    onSuccess: async (updated) => {
      setSelectedTransfer(updated);
      setTransferExceptionMode(null);
      setTransferExceptionComment("");
      setTransferDestinationId("");
      setTab(updated.status === "OPEN" ? "requests" : "closed");
      await queryClient.invalidateQueries({ queryKey: ["process-transfers"] });
      await queryClient.invalidateQueries({ queryKey: ["quality-inspections"] });
      await queryClient.invalidateQueries({ queryKey: ["repackaging-tasks"] });
    }
  });
  const alerts = useQuery({
    queryKey: ["putaway-alerts"],
    queryFn: loadPutawayAlerts
  });
  const initialTaskOpened = useRef(false);
  useEffect(() => {
    if (initialTaskOpened.current || !initialTaskId
        || !tasks.data || !transfers.data || !alerts.data) return;
    const target = tasks.data.find((task) => task.id === initialTaskId);
    if (target) {
      initialTaskOpened.current = true;
      setTab(["COMPLETED", "CANCELLED"].includes(target.status)
        ? "closed" : "tasks");
      if (initialOpenDetail) setExecutionTask(target);
      window.setTimeout(() => document.getElementById(
        `putaway-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    const transfer = transfers.data.find((task) => task.id === initialTaskId);
    if (transfer) {
      initialTaskOpened.current = true;
      setTab(transfer.status === "COMPLETED" ? "closed"
        : transfer.status === "EXCEPTION" ? "alerts" : "tasks");
      if (initialOpenDetail) setSelectedTransfer(transfer);
      window.setTimeout(() => document.getElementById(
        `putaway-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    const alert = alerts.data.find((entry) => entry.id === initialTaskId);
    if (alert) {
      initialTaskOpened.current = true;
      setTab("alerts");
      if (initialOpenDetail
          && alert.resolutionAction === "PACKAGING_IDENTIFICATION") {
        setPackagingAlert(alert);
        setPackagingId("");
        setPackagingComment("");
      }
      window.setTimeout(() => document.getElementById(
        `putaway-${initialTaskId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    }
  }, [alerts.data, initialOpenDetail, initialTaskId, tasks.data, transfers.data]);
  const packagingOptions = useQuery({
    queryKey: ["putaway-packaging-options", packagingAlert?.itemId],
    queryFn: () => loadPackaging(packagingAlert!.itemId),
    enabled: packagingAlert !== null
  });
  const simulate = useMutation({
    mutationFn: (requestId: string) => simulatePutaway(requestId),
    onSuccess: async (simulation) => {
      setResult(simulation);
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
      if (simulation.status === "EXCEPTION") {
        setTab("alerts");
      }
    }
  });
  useEffect(() => {
    if (initialTaskOpened.current || !initialTaskId || !requests.data) return;
    const request = requests.data.find((entry) => entry.id === initialTaskId);
    if (!request) return;
    initialTaskOpened.current = true;
    setTab("requests");
    setIdentifierFilter(request.huNumber);
    setSelected(request);
    if (initialOpenDetail) simulate.mutate(request.id);
    window.setTimeout(() => document.getElementById(
      `putaway-request-${request.id}`
    )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [initialOpenDetail, initialTaskId, requests.data]);
  const loadDiagnostic = useMutation({
    mutationFn: (decisionId: string) => loadPutawayDecision(decisionId),
    onSuccess: (simulation) => {
      setSelected((requests.data ?? []).find(
        (request) => request.id === simulation.requestId
      ) ?? null);
      setResult(simulation);
    }
  });
  const identifyPackaging = useMutation({
    mutationFn: () => identifyPutawayPackaging(
      packagingAlert!.id, packagingId, packagingComment.trim()
    ),
    onSuccess: async (simulation) => {
      setPackagingAlert(null);
      setPackagingId("");
      setPackagingComment("");
      setResult(simulation);
      setTab("requests");
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    }
  });
  const resimulateAlert = useMutation({
    mutationFn: () => resimulatePutawayAlert(
      resimulationAlert!.id,
      releaseRejectedBin,
      resimulationComment.trim()
    ),
    onSuccess: async (simulation) => {
      setResimulationAlert(null);
      setReleaseRejectedBin(false);
      setResimulationComment("");
      setResult(simulation);
      setSelected((requests.data ?? []).find(
        (request) => request.id === simulation.requestId
      ) ?? null);
      setTab(simulation.status === "PROPOSED" ? "requests" : "alerts");
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    }
  });
  const accept = useMutation({
    mutationFn: (simulation: PutawaySimulation) => acceptPutaway(
      simulation.requestId,
      simulation.decisionId!,
      simulation.requestVersion
    ),
    onSuccess: async (task) => {
      setCreatedTask(task);
      setResult(null);
      setSelected(null);
      setTab("tasks");
      await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
    }
  });
  const activeTourTaskIds = useMemo(() => new Set(
    (tours.data ?? [])
      .filter((tour) => ![
        "COMPLETED", "COMPLETED_WITH_INCIDENT", "CANCELLED"
      ].includes(tour.status))
      .flatMap((tour) => tour.steps.map((step) => step.taskId))
  ), [tours.data]);
  const activeTasks = useMemo(() => [...(tasks.data ?? [])]
    .filter((task) => !["COMPLETED", "CANCELLED"].includes(task.status))
    // Une tâche orientée vers une file doit obligatoirement être groupée.
    // Elle ne redevient exécutable que depuis la tournée constituée.
    .filter((task) => !task.workQueueId || Boolean(task.tourNumber))
    // Une HU déjà placée dans une tournée se pilote depuis la tournée : ne pas
    // la présenter une seconde fois comme une tâche individuelle concurrente.
    .filter((task) => !activeTourTaskIds.has(task.id))
    .filter((task) =>
      (!identifierFilter || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`
      }, identifierFilter))
      && (!contextFilter || task.status === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`,
        destination: task.destinationBinCode,
        gare: task.sourcePointCode,
        engin:
          `${task.requiredEquipmentTypeCode} ${task.assignedEquipmentNumber ?? ""}`,
        statut: task.status,
        date: `${task.reservationExpiresAt} ${task.claimedAt ?? ""}`
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left.claimedAt ?? left.reservationExpiresAt,
      right.claimedAt ?? right.reservationExpiresAt,
      sortDirection
    )), [
      activeTourTaskIds, contextFilter, identifierFilter, sortDirection,
      syntaxSearch, tasks.data
    ]);
  const supersededTaskIds = useMemo(() => new Set(
    (alerts.data ?? [])
      .filter((alert) => Boolean(alert.replacementTaskId))
      .map((alert) => alert.taskId)
  ), [alerts.data]);
  const closedTasks = useMemo(() => [...(tasks.data ?? [])]
    .filter((task) => ["COMPLETED", "CANCELLED"].includes(task.status))
    // Une tâche annulée qui a été replanifiée est une ancienne tentative,
    // pas l'état final de la HU. Elle reste visible dans l'alerte, le
    // diagnostic et l'audit, tandis que la tâche de remplacement porte
    // désormais l'état opérationnel.
    .filter((task) => task.status !== "CANCELLED"
      || !supersededTaskIds.has(task.id))
    .filter((task) =>
      (!identifierFilter || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`
      }, identifierFilter))
      && (!contextFilter || task.status === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`,
        destination: task.destinationBinCode,
        gare: task.sourcePointCode,
        engin:
          `${task.requiredEquipmentTypeCode} ${task.assignedEquipmentNumber ?? ""}`,
        statut: task.status,
        date: task.completedAt
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left.completedAt, right.completedAt, sortDirection
    )), [
      contextFilter, identifierFilter, sortDirection, supersededTaskIds,
      syntaxSearch, tasks.data
    ]);
  const closedTours = useMemo(() => [...(tours.data ?? [])]
    .filter((tour) => [
      "COMPLETED", "COMPLETED_WITH_INCIDENT", "CANCELLED"
    ].includes(tour.status))
    // Une tournée annulée dont toutes les étapes ont été remplacées ne doit
    // plus apparaître comme résultat final dans Terminées.
    .filter((tour) => tour.status !== "CANCELLED"
      || !tour.steps.every((step) => supersededTaskIds.has(step.taskId)))
    .filter((tour) =>
      (!identifierFilter || matchesListSyntax({
        id: `${tour.number} ${tour.steps.map((step) => step.huNumber).join(" ")}`
      }, identifierFilter))
      && (!contextFilter || tour.status === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${tour.number} ${tour.steps.map((step) => step.huNumber).join(" ")}`,
        destination: tour.steps.map((step) => step.destinationBinCode).join(" "),
        engin: tour.equipmentNumber ?? "",
        statut: tour.status,
        date: tour.completedAt ?? tour.createdAt
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left.completedAt, right.completedAt, sortDirection
    )), [
      contextFilter, identifierFilter, sortDirection, supersededTaskIds,
      syntaxSearch, tours.data
    ]);
  const closedTourTaskIds = useMemo(() => new Set(
    closedTours.flatMap((tour) => tour.steps.map((step) => step.taskId))
  ), [closedTours]);
  const closedStandaloneTasks = useMemo(() => closedTasks.filter(
    (task) => !closedTourTaskIds.has(task.id)
  ), [closedTasks, closedTourTaskIds]);
  const activeAlerts = useMemo(() => [...(alerts.data ?? [])]
    .filter((alert) => !["RESOLVED", "CANCELLED"].includes(alert.status))
    .filter((alert) =>
      (!identifierFilter || matchesListSyntax({
        id: `${alert.id} ${alert.huNumber}`
      }, identifierFilter))
      && (!contextFilter || alert.reasonCode === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${alert.id} ${alert.huNumber}`,
        emplacement: alert.rejectedBinCode,
        raison: `${alert.reasonCode} ${alert.comment}`,
        statut: alert.status,
        action: alert.resolutionAction,
        date: alert.reportedAt
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left.reportedAt, right.reportedAt, sortDirection
    )), [
      alerts.data, contextFilter, identifierFilter, sortDirection, syntaxSearch
    ]);
  const repackagingAlerts = useMemo(() => [...(repackaging.data ?? [])]
    .filter((task) => task.status === "EXCEPTION")
    .filter((task) =>
      (!identifierFilter || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`
      }, identifierFilter))
      && (!contextFilter || task.exceptionCode === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${task.id} ${task.huNumber}`,
        article: task.itemCode,
        gare: task.processPointCode,
        raison: `${task.exceptionCode} ${task.exceptionComment}`,
        statut: task.status,
        date: task.exceptionReportedAt
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left.exceptionReportedAt, right.exceptionReportedAt, sortDirection
    )), [
      contextFilter, identifierFilter, repackaging.data, sortDirection,
      syntaxSearch
    ]);
  const filteredRequests = useMemo(() => [...(requests.data ?? [])]
    .filter((request) => request.status !== "EXCEPTION")
    .filter((request) =>
      (!identifierFilter || matchesListSyntax({
        id: `${request.id} ${request.huNumber}`
      }, identifierFilter))
      && (!contextFilter || request.warehouseCode === contextFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${request.id} ${request.huNumber}`,
        article: request.itemCode,
        entrepot: request.warehouseCode,
        client: `${request.clientCode ?? ""} ${request.clientName ?? ""}`,
        gare: request.sourcePointCode,
        statut: request.status,
        raison: request.exceptionReasonCode,
        action: request.resolutionAction
      }, syntaxSearch))
    ), [
      contextFilter, identifierFilter, requests.data, syntaxSearch
    ]);
  const contextValues = tab === "requests"
    ? [...new Set(requests.data?.map((request) => request.warehouseCode) ?? [])]
    : tab === "alerts"
      ? [...new Set([
        ...(alerts.data
          ?.filter((alert) => !["RESOLVED", "CANCELLED"].includes(alert.status))
          .map((alert) => alert.reasonCode) ?? []),
        ...(repackaging.data
          ?.filter((task) => task.status === "EXCEPTION")
          .map((task) => task.exceptionCode ?? "OTHER") ?? [])
      ])]
      : [...new Set(tasks.data
        ?.filter((task) => tab === "closed"
          ? ["COMPLETED", "CANCELLED"].includes(task.status)
          : !["COMPLETED", "CANCELLED"].includes(task.status))
        .map((task) => task.status) ?? [])];
  const contextLabel = tab === "requests" ? t("putaway.filters.warehouse")
    : tab === "alerts" ? t("putaway.filters.reason") : t("putaway.filters.status");
  const diagnosticAlert = result
    ? (alerts.data ?? []).find((alert) => alert.requestId === result.requestId)
    : null;
  const statusTabs = [
    ...(filteredRequests.length > 0 ? [{ value: "requests" as const,
      label: t("putaway.tabs.simulate"), mobileLabel: t("putaway.tabsMobile.simulate"),
      count: filteredRequests.length }] : []),
    { value: "queues" as const, label: t("putaway.tabs.queues"), mobileLabel: t("putaway.tabsMobile.queues"),
      count: (workQueues.data?.reduce((total, queue) =>
        total + queue.waitingTaskCount, 0) ?? 0)
        + (tours.data?.filter((tour) => tour.status === "LOADING")
          .reduce((total, tour) => total + tour.steps.length, 0) ?? 0) },
    { value: "tours" as const, label: t("putaway.tabs.tours"), mobileLabel: t("putaway.tabsMobile.tours"),
      count: tours.data?.filter((tour) =>
        ![
          "LOADING", "COMPLETED", "COMPLETED_WITH_INCIDENT", "CANCELLED"
        ].includes(tour.status))
        .length ?? 0 },
    { value: "tasks" as const, label: t("putaway.tabs.tasks"), mobileLabel: t("putaway.tabsMobile.tasks"),
      count: activeTasks.length + (transfers.data?.filter((task) =>
        task.status === "IN_PROGRESS").length ?? 0) },
    { value: "alerts" as const, label: t("putaway.tabs.alerts"), mobileLabel: t("putaway.tabsMobile.alerts"),
      count: activeAlerts.length + repackagingAlerts.length
        + (transfers.data?.filter((task) => task.status === "EXCEPTION")
          .length ?? 0) },
    { value: "closed" as const, label: t("putaway.tabs.closed"), mobileLabel: t("putaway.tabsMobile.closed"),
      count: closedTours.length + closedStandaloneTasks.length }
  ];
  const selectedMobileRequest = tab === "requests"
    && mobileSelection?.kind === "request"
    ? filteredRequests.find((request) => request.id === mobileSelection.id) ?? null
    : null;
  const selectedMobileTour = tab === "tours"
    && mobileSelection?.kind === "tour"
    ? (tours.data ?? []).find((tour) => tour.id === mobileSelection.id) ?? null
    : null;
  const selectedMobileTask = tab === "tasks"
    && mobileSelection?.kind === "task"
    ? activeTasks.find((task) => task.id === mobileSelection.id) ?? null
    : null;
  const selectedMobileMissionDocumentTask = selectedMobileTour?.steps
    .map((step) => tasks.data?.find((task) => task.id === step.taskId))
    .find((task) => task?.missionDocumentId) ?? null;
  const selectedMobileTourCanCancel = Boolean(selectedMobileTour
    && ["LOADING", "READY", "EXCEPTION"].includes(selectedMobileTour.status));
  const selectedMobileQueue = tab === "queues" && expandedQueueId
    ? workQueues.data?.find((queue) => queue.id === expandedQueueId) ?? null
    : null;
  const mobilePrimaryLabel = selectedMobileRequest ? t("putaway.actions.simulate")
    : selectedMobileQueue ? (selectedMobileQueue.groupingMode === "MULTI_HU"
      ? t("putaway.actions.buildRoll") : t("putaway.actions.buildMission"))
      : selectedMobileTour ? (selectedMobileTour.status === "LOADING"
        ? t("putaway.actions.resumeLoading")
        : selectedMobileTour.status === "READY" ? t("putaway.actions.start") : t("putaway.actions.execute"))
        : selectedMobileTask ? (selectedMobileTask.status === "IN_PROGRESS"
          ? t("putaway.actions.confirmDeposit")
          : selectedMobileTask.status === "ASSIGNED"
            ? t("putaway.actions.confirmClaim") : t("putaway.actions.claim"))
          : t("putaway.actions.selectActivity");
  const mobilePrimaryAvailable = Boolean(selectedMobileRequest
    || (selectedMobileQueue && selectedQueueTaskIds.length > 0)
    || selectedMobileTour || selectedMobileTask);
  useEffect(() => {
    const task = executionTask ?? createdTask ?? selectedMobileTask;
    const tour = loadingTour ?? selectedMobileTour;
    const request = selected ?? selectedMobileRequest;
    const huNumber = task?.huNumber ?? request?.huNumber
      ?? tour?.steps[0]?.huNumber ?? null;
    setDiagnosticContext({
      screen: t("putaway.module"),
      action: executionTask ? t("putaway.actions.confirmDeposit")
        : loadingTour ? t("putaway.labels.loadingEquipment")
          : selected ? t("putaway.actions.simulate") : t(`putaway.tabs.${tab}`),
      entities: [
        ...(tour ? [{ type: "TOUR" as const, identifier: tour.number,
          status: tour.status }] : []),
        ...(huNumber ? [{ type: "HU" as const, identifier: huNumber,
          status: task?.status ?? request?.status }] : []),
        ...(request?.asnNumber ? [{ type: "ASN" as const,
          identifier: request.asnNumber }] : []),
        ...(request?.receiptNumber ? [{ type: "REC" as const,
          identifier: request.receiptNumber }] : []),
        ...(task?.destinationBinCode ? [{ type: "LOCATION" as const,
          identifier: task.destinationBinCode }] : [])
      ]
    });
  }, [executionTask, createdTask, selectedMobileTask, loadingTour,
    selectedMobileTour, selected, selectedMobileRequest, tab]);
  const executeMobilePrimary = () => {
    if (selectedMobileRequest) {
      setSelected(selectedMobileRequest);
      setResult(null);
      simulate.mutate(selectedMobileRequest.id);
      return;
    }
    if (selectedMobileQueue) {
      if (selectedMobileQueue.groupingMode === "SINGLE_HU") {
        const candidate = (queueCandidates.data ?? []).find((entry) =>
          entry.taskId === selectedQueueTaskIds[0]);
        if (candidate) void startDirectMission(selectedMobileQueue.id, candidate);
      } else {
        createTour.mutate({ queueId: selectedMobileQueue.id,
          taskIds: selectedQueueTaskIds });
      }
      return;
    }
    if (selectedMobileTour) {
      if (selectedMobileTour.status === "LOADING") {
        setLoadingTour(selectedMobileTour);
        setTourScan("");
        setSkippedTourStepIds([]);
      } else {
        setExecutionTourId(selectedMobileTour.id);
      }
      return;
    }
    if (selectedMobileTask) setExecutionTask(selectedMobileTask);
  };

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" },
      minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" },
      ...(initialTaskId ? {
        [`& #putaway-${initialTaskId}`]: {
          borderColor: "primary.main",
          borderWidth: 2
        }
      } : {})
    }}>
      <MobileOperationalHeader moduleLabel={t("putaway.module")}
        search={syntaxSearch} onSearchChange={setSyntaxSearch}
        searchPlaceholder={t("putaway.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("putaway.title")} compact
          backLabel={t("common.home")} onBack={onBack} />
      </Box>

      <ResponsiveStatusTabs value={tab} items={statusTabs}
        onChange={(value) => {
          setTab(value);
          setResult(null);
          setContextFilter("");
          setMobileSelection(null);
          setExpandedQueueId(null);
          setSelectedQueueTaskIds([]);
          // Les compteurs d'onglets et leur contenu proviennent de requêtes
          // distinctes. Au changement d'onglet, supprimer les anciens
          // candidats puis recharger l'ensemble évite d'afficher un compteur
          // ancien avec une liste déjà actualisée (ou l'inverse).
          queryClient.removeQueries({
            queryKey: ["putaway-queue-candidates"]
          });
          void Promise.all([
            queryClient.refetchQueries({ queryKey: ["putaway-requests"] }),
            queryClient.refetchQueries({ queryKey: ["putaway-tasks"] }),
            queryClient.refetchQueries({ queryKey: ["putaway-work-queues"] }),
            queryClient.refetchQueries({ queryKey: ["putaway-tours"] }),
            queryClient.refetchQueries({ queryKey: ["putaway-alerts"] }),
            queryClient.refetchQueries({ queryKey: ["process-transfers"] }),
            queryClient.refetchQueries({ queryKey: ["repackaging-tasks"] })
          ]);
        }} />

      {!['queues', 'tours'].includes(tab) && <Card variant="outlined"
        sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }}>
        <CardContent sx={{
          py: 1.25,
          "&:last-child": { pb: 1.25 }
        }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5}>
            <TextField size="small" label={t("putaway.filters.id")} value={identifierFilter}
              onChange={(event) => setIdentifierFilter(event.target.value)}
              sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel>{contextLabel}</InputLabel>
              <Select label={contextLabel} value={contextFilter}
                onChange={(event) => setContextFilter(event.target.value)}>
                <MenuItem value="">{t("common.filters.all")}</MenuItem>
                {contextValues.sort().map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label={t("common.filters.search")}
              placeholder={t("putaway.filters.syntax")}
              value={syntaxSearch}
              onChange={(event) => setSyntaxSearch(event.target.value)}
              InputProps={{ startAdornment:
                <SearchOutlined color="action" sx={{ mr: 1 }} /> }}
              sx={{ flex: 2 }} />
            {tab !== "requests" && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t("putaway.filters.dateOrder")}</InputLabel>
                <Select label={t("putaway.filters.dateOrder")} value={sortDirection}
                  onChange={(event) => setSortDirection(event.target.value as
                    "asc" | "desc")}>
                  <MenuItem value="asc">{t("common.filters.ascending")}</MenuItem>
                  <MenuItem value="desc">{t("common.filters.descending")}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </CardContent>
      </Card>}

      <Box sx={{
        minHeight: 0,
        flex: { xs: "1 1 0", sm: "none", md: 1 },
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5,
        pb: { xs: 11, md: 0 }
      }}>
      {tab === "queues" && (
        <Stack spacing={1.5}>
          <Alert severity="info">
            {t("putaway.help.queues")}
          </Alert>
          {(tours.data ?? []).filter((tour) => tour.status === "LOADING")
            .map((tour) => (
              <Alert key={tour.id} severity="warning"
                action={<Button color="inherit" onClick={() => {
                  setLoadingTour(tour); setTourScan("");
                  setSkippedTourStepIds([]);
                }}>{t("putaway.actions.resumeLoading")}</Button>}>
                <strong>{tour.number}</strong> · {t("putaway.labels.loadingEquipment")}
                {` (${tour.steps.filter((step) => step.loaded).length}/${tour.steps.length} HU)`}
              </Alert>
            ))}
          {workQueues.isLoading && <CircularProgress />}
          {workQueues.isError && (
            <Alert severity="error">{t("putaway.errors.queues")}</Alert>
          )}
          {(workQueues.data ?? []).map((queue) => (
            <Card key={queue.id} variant="outlined"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest(
                  "button,input,[role='button']")) return;
                setExpandedQueueId((current) =>
                  current === queue.id ? null : queue.id);
                setMobileSelection(null);
              }}
              sx={{ cursor: { xs: "pointer", sm: "default" },
                outline: { xs: expandedQueueId === queue.id
                  ? "2px solid" : "none", sm: "none" },
                outlineColor: "primary.main" }}>
              <CardContent sx={{ px: { xs: 1.5, sm: 2 },
                "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                  alignItems={{ md: "center" }}>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={850} sx={{ overflowWrap: "anywhere" }}>
                      {workQueueLabel(queue.code, queue.name)}
                    </Typography>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                      <LabeledInfo label={t("putaway.card.huCapacity")} prominent>
                        {queue.waitingTaskCount} / {queue.maxHandlingUnits}
                      </LabeledInfo>
                      <LabeledInfo label={t(queue.maxWeightKg != null ? "putaway.card.weightCapacity" : "putaway.card.waitingWeight")} prominent>
                        {weightLabel(queue.waitingWeightKg)}
                        {queue.maxWeightKg != null && ` / ${weightLabel(queue.maxWeightKg)}`}
                      </LabeledInfo>
                      {queue.maxVolumeMm3 != null && <LabeledInfo label={t("putaway.card.maxVolume")}>{volumeLabel(queue.maxVolumeMm3)}</LabeledInfo>}
                    </Stack>
                  </Stack>
                  <IconButton className="mobile-card-expand"
                    aria-label={t("putaway.labels.eligibleHu")}
                    onClick={() => setExpandedQueueId((current) =>
                      current === queue.id ? null : queue.id)}
                    sx={{ transform: expandedQueueId === queue.id
                      ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                    <ExpandMore />
                  </IconButton>
                  <Button variant="contained"
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                    disabled={queue.waitingTaskCount === 0 || createTour.isPending
                      || expandedQueueId !== queue.id
                      || selectedQueueTaskIds.length === 0}
                    onClick={() => {
                      if (queue.groupingMode === "SINGLE_HU") {
                        const candidate = (queueCandidates.data ?? []).find(
                          (entry) => entry.taskId === selectedQueueTaskIds[0]
                        );
                        if (candidate) void startDirectMission(queue.id, candidate);
                        return;
                      }
                      createTour.mutate({
                        queueId: queue.id, taskIds: selectedQueueTaskIds
                      });
                    }}>
                    {queue.groupingMode === "MULTI_HU"
                      ? t("putaway.actions.buildRoll") : t("putaway.actions.buildMission")}
                  </Button>
                </Stack>
                <Collapse in={expandedQueueId === queue.id} unmountOnExit>
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: 1,
                    borderColor: "divider" }}>
                    {queueCandidates.isLoading && <CircularProgress size={24} />}
                    {queueCandidates.isError && (
                      <Alert severity="error">
                        {t("putaway.errors.eligibleHu")}
                      </Alert>
                    )}
                    {(queueCandidates.data ?? []).map(
                      (candidate: PutawayQueueCandidate) => {
                        const checked = selectedQueueTaskIds.includes(candidate.taskId);
                        const selectedCandidates = (queueCandidates.data ?? [])
                          .filter((entry) => selectedQueueTaskIds.includes(entry.taskId));
                        const selectedWeight = selectedCandidates.reduce(
                          (sum, entry) => sum + entry.weightKg, 0
                        );
                        const selectedVolume = selectedCandidates.reduce(
                          (sum, entry) => sum + entry.volumeMm3, 0
                        );
                        const capacityReason = checked ? null
                          : selectedQueueTaskIds.length >= queue.maxHandlingUnits
                            ? t("putaway.labels.queueLimitReached", { count: queue.maxHandlingUnits })
                            : queue.maxWeightKg != null
                              && selectedWeight + candidate.weightKg > queue.maxWeightKg
                              ? `${t("putaway.labels.capacityExceeded")} ${weightLabel(
                                selectedWeight + candidate.weightKg
                              )} > ${weightLabel(queue.maxWeightKg)}`
                              : queue.maxVolumeMm3 != null
                                && selectedVolume + candidate.volumeMm3 > queue.maxVolumeMm3
                                ? `${volumeLabel(selectedVolume
                                  + candidate.volumeMm3)} > ${volumeLabel(queue.maxVolumeMm3)}`
                                : null;
                        return (
                          <Box key={candidate.taskId} sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "44px minmax(0,1fr)",
                              md: "44px minmax(160px,1fr) minmax(120px,1fr) auto auto"
                            },
                            gap: 1, alignItems: "center", py: .75
                          }}>
                            <Tooltip title={capacityReason ?? ""} arrow>
                              <span>
                                <Checkbox checked={checked} sx={{ minWidth: 44, minHeight: 44 }}
                                  inputProps={{
                                    "aria-label": t("putaway.labels.selectHu", { hu: candidate.huNumber }),
                                    "aria-description": capacityReason ?? undefined
                                  }}
                                  disabled={capacityReason != null}
                                  onChange={() => setSelectedQueueTaskIds((current) =>
                                    checked
                                      ? current.filter((id) => id !== candidate.taskId)
                                      : [...current, candidate.taskId])} />
                              </span>
                            </Tooltip>
                            <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                              <LabeledInfo label={t("putaway.card.hu")} prominent>{candidate.huNumber}</LabeledInfo>
                              <Typography variant="body2">
                                <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>{t("putaway.card.item")} : </Box>
                                {candidate.itemCode}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: 2, md: "auto" } }}>
                              <LabeledInfo label={t("putaway.card.destination")} prominent>{candidate.destinationBinCode}</LabeledInfo>
                            </Box>
                            <Box sx={{ gridColumn: { xs: 2, md: "auto" } }}>
                              <LabeledInfo label={t("putaway.card.weight")}>{weightLabel(candidate.weightKg)}</LabeledInfo>
                            </Box>
                            <Button size="small" variant="outlined"
                              sx={{ display: { xs: "none", sm: "inline-flex" }, gridColumn: { xs: 2, md: "auto" }, minHeight: 44 }}
                              data-testid={`putaway-direct-mission-${candidate.huNumber}`}
                              disabled={createTour.isPending}
                              onClick={() => void startDirectMission(
                                queue.id, candidate
                              )}>
                              {t("putaway.actions.directMission")}
                            </Button>
                          </Box>
                        );
                      })}
                    {!queueCandidates.isLoading
                      && (queueCandidates.data?.length ?? 0) === 0 && (
                      <Alert severity="info">{t("putaway.empty.eligibleHu")}</Alert>
                    )}
                    {selectedQueueTaskIds.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 750 }}>
                        {t("putaway.labels.configuredSelection", {
                          count: selectedQueueTaskIds.length,
                          weight: weightLabel((queueCandidates.data ?? [])
                            .filter((candidate) => selectedQueueTaskIds.includes(
                              candidate.taskId))
                            .reduce((sum, candidate) => sum + candidate.weightKg, 0)),
                          limit: queue.maxWeightKg == null ? ""
                            : t("putaway.labels.configuredWeight", {
                              weight: weightLabel(queue.maxWeightKg)
                            })
                        })}
                      </Typography>
                    )}
                    {createTour.isError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {createTour.error instanceof Error
                          ? createTour.error.message
                          : t("putaway.errors.tourCreate")}
                      </Alert>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
      {tab === "tours" && (
        <Stack spacing={1.5}>
          {tours.isLoading && <CircularProgress />}
          {tours.isError && (
            <Alert severity="error">{t("putaway.errors.tours")}</Alert>
          )}
          {(tours.data ?? []).filter((tour) =>
            ["LOADING", "READY", "IN_PROGRESS", "EXCEPTION"].includes(tour.status))
            .map((tour: PutawayTour) => {
            const nextStep = tour.steps.find((step) =>
              !["COMPLETED", "CANCELLED"].includes(step.taskStatus));
            const nextTask = nextStep
              ? tasks.data?.find((task) => task.id === nextStep.taskId) : null;
            const missionDocumentTask = tour.steps
              .map((step) => tasks.data?.find((task) => task.id === step.taskId))
              .find((task) => task?.missionDocumentId);
            return (
              <Card key={tour.id} variant="outlined"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest(
                    "button,a,[role='button']")) return;
                  setMobileSelection({ kind: "tour", id: tour.id });
                }}
                sx={{ cursor: { xs: "pointer", sm: "default" },
                  outline: { xs: mobileSelection?.kind === "tour"
                    && mobileSelection.id === tour.id
                    ? "2px solid" : "none", sm: "none" },
                  outlineColor: "primary.main" }}>
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
                    alignItems={{ md: "center" }}>
                    <Stack direction={{ xs: "column", md: "row" }}
                      spacing={1.5} sx={{ flex: 1,
                      minWidth: 0, width: { xs: "100%", md: "auto" } }}>
                      <AssignmentOutlined color="secondary" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                            <LabeledInfo label={t("putaway.card.mission")} prominent>{tour.number}</LabeledInfo>
                            <LabeledInfo label={t("putaway.card.queue")}>{workQueueLabel(tour.workQueueCode, tour.workQueueName)}</LabeledInfo>
                          </Stack>
                          <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                            <LabeledInfo label={t("putaway.card.huCount")}>{tour.steps.length}</LabeledInfo>
                            <LabeledInfo label={t("putaway.card.route")}>{tour.routingStrategy === "LOCATION_SEQUENCE"
                              ? t("putaway.labels.optimizedSequence") : t("putaway.labels.simpleSequence")}</LabeledInfo>
                            {tour.executionMode === "DIRECT" && <Chip size="small" variant="outlined" label={t("putaway.labels.directOneHu")} />}
                            {tour.equipmentNumber && <LabeledInfo label={t("putaway.card.equipment")}>{tour.equipmentNumber}</LabeledInfo>}
                            <LabeledInfo label={t("putaway.card.distance")}>{tour.estimatedDistanceUnits} u</LabeledInfo>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                    {tour.status === "LOADING" && (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => {
                          setLoadingTour(tour); setTourScan("");
                          setSkippedTourStepIds([]);
                        }}>
                          {t("putaway.actions.resumeLoading")}
                        </Button>
                    )}
                    {nextTask && tour.status !== "LOADING" && (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        data-testid={`putaway-open-tour-${nextTask.huNumber}`}
                        onClick={() => setExecutionTourId(tour.id)}>
                        {tour.status === "READY" ? t("putaway.actions.start") : t("putaway.actions.continueStep")}
                      </Button>
                    )}
                    {missionDocumentTask && (
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <PutawayMissionDocumentLink task={missionDocumentTask} />
                      </Box>
                    )}
                    {["LOADING", "READY", "EXCEPTION"].includes(tour.status) && (
                      <Button color="error" disabled={cancelTour.isPending}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => {
                          if (window.confirm(t("putaway.confirmations.cancelMission", { number: tour.number }))) {
                            cancelTour.mutate(tour);
                          }
                        }}>
                        {t("common.actions.cancel")}
                      </Button>
                    )}
                    <ListRowActions status={
                      <Chip label={operationalStatusLabel(tour.status)}
                        color={operationalStatusColor(tour.status)} />
                    } />
                    <IconButton className="mobile-card-expand"
                      aria-label={t("putaway.labels.detailsMission")}
                      data-testid={`putaway-tour-expand-${tour.number}`}
                      onClick={() => setExpandedTourId((current) =>
                        current === tour.id ? null : tour.id)}
                      sx={{ alignSelf: { xs: "center", md: "auto" },
                        width: 40, height: 40,
                        transform: expandedTourId === tour.id
                        ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                      <ExpandMore />
                    </IconButton>
                  </Stack>
                  <Collapse in={expandedTourId === tour.id}>
                    <Stack spacing={.75} sx={{ mt: 1.5, pt: 1.5,
                      borderTop: 1, borderColor: "divider" }}>
                      {tour.steps.map((step) => (
                        <Box key={step.id} sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "28px minmax(0, 1fr) auto",
                            md: "36px minmax(150px, 1fr) minmax(140px, 1fr) auto" },
                          gap: 1, alignItems: "center", py: .5,
                          color: step.taskStatus === "COMPLETED"
                            ? "text.secondary" : "text.primary"
                        }}>
                          <Typography fontWeight={800}>{step.sequence}.</Typography>
                          <Typography fontWeight={750} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{step.huNumber}</Typography>
                          <Typography sx={{
                            gridColumn: { xs: "2 / 4", md: "auto" },
                            gridRow: { xs: 2, md: "auto" }
                          }}>
                            <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>{t("putaway.card.destination")} : </Box>
                            {step.destinationBinCode}
                            {` · ${t("putaway.labels.distance", { distance: step.distanceFromPrevious })}`}
                          </Typography>
                          <Chip size="small"
                            sx={{ gridColumn: { xs: 3, md: "auto" },
                              gridRow: { xs: 1, md: "auto" },
                              justifySelf: { xs: "end", md: "auto" } }}
                            label={tour.status === "LOADING"
                              ? (step.loaded ? t("putaway.labels.scanned") : t("putaway.labels.toScan"))
                              : operationalStatusLabel(step.taskStatus)}
                            color={tour.status === "LOADING"
                              ? (step.loaded ? "success" : "warning")
                              : operationalStatusColor(step.taskStatus)} />
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
          {!tours.isLoading && (tours.data?.filter((tour) =>
            ["LOADING", "READY", "IN_PROGRESS", "EXCEPTION"].includes(tour.status)
          ).length ?? 0) === 0 && (
            <Alert severity="info">
                {t("putaway.empty.tours")}
            </Alert>
          )}
        </Stack>
      )}
      {tab === "requests" && (
        <>
          {requests.isLoading && <CircularProgress />}
          {requests.isError && (
            <Alert severity="error">
              {t("putaway.errors.requests")}
            </Alert>
          )}
          {requests.data?.filter((request) =>
            request.status !== "EXCEPTION").length === 0 && (
            <Alert severity="info">
              {t("putaway.empty.requests")}
            </Alert>
          )}

          <Stack spacing={1.5}>
            {transfers.data?.filter((task) => task.status === "OPEN")
              .map((task) => (
                <Card key={task.id} id={`putaway-${task.id}`} variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                      alignItems={{ md: "center" }}>
                      <AssignmentOutlined color="secondary" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography fontWeight={800}>
                          {task.huNumber} → {task.destinationPointCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {transferLabel(task, t)}
                          {` · ${t("putaway.labels.source", { source: task.sourcePointCode })}`}
                        </Typography>
                      </Box>
                      <ListRowActions
                        actions={
                          <Button variant="contained"
                            data-testid={`transfer-take-${task.id}`}
                            disabled={transferAction.isPending}
                            onClick={() => transferAction.mutate(task)}>
                            {t("putaway.actions.claim")}
                          </Button>
                        }
                        status={<Chip label={operationalStatusLabel("OPEN")} color="warning" />}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            {filteredRequests.map((request) => (
              <Card key={request.id} id={`putaway-request-${request.id}`}
                variant="outlined"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest(
                    "button,a,[role='button']")) return;
                  setMobileSelection({ kind: "request", id: request.id });
                }}
                sx={{
                  cursor: { xs: "pointer", sm: "default" },
                  borderColor: selected?.id === request.id
                    ? "primary.main" : undefined
                  , outline: { xs: mobileSelection?.kind === "request"
                    && mobileSelection.id === request.id
                    ? "2px solid" : "none", sm: "none" },
                  outlineColor: "primary.main"
                }}>
                <CardContent>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                    alignItems={{ sm: "center" }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {request.huNumber} · {request.itemCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {request.quantity} {request.uom}
                        {" · "}{request.warehouseCode}
                        {request.clientName
                          ? ` · ${t("common.client")} ${request.clientName}${request.clientCode
                            ? ` (${request.clientCode})` : ""}`
                          : ""}
                        {` · ${t("putaway.labels.source", { source: request.sourcePointCode })}`}
                      </Typography>
                      <SourceTrace sources={[
                        request.asnNumber, request.receiptNumber, request.huNumber,
                        request.qualityInspectionNumber
                      ]} step={t("putaway.module")} client={request.clientName
                        ?? request.clientCode} />
                      {request.status === "EXCEPTION" && (
                        <Typography variant="body2" color="error.main"
                          sx={{ mt: 0.5, fontWeight: 700 }}>
                          {request.resolutionAction
                            ? resolutionLabel(t, request.resolutionAction)
                            : t("putaway.labels.toProcess")}
                          {request.exceptionReasonCode
                            ? ` · ${reasonLabel(t, request.exceptionReasonCode)}` : ""}
                          {request.complementaryTaskStatus
                            ? ` · ${request.complementaryTaskStatus}` : ""}
                        </Typography>
                      )}
                    </Box>
                    <ListRowActions
                      actions={request.status === "EXCEPTION" ? (
                        <Button variant="outlined"
                          startIcon={<WarningAmberOutlined />}
                          onClick={() => {
                            setSelected(null);
                            setResult(null);
                            setTab("alerts");
                          }}>
                          {t("putaway.actions.viewAlert")}
                        </Button>
                      ) : (
                        <Button variant="contained"
                          data-testid={`putaway-simulate-${request.huNumber}`}
                          sx={{ display: { xs: "none", sm: "inline-flex" } }}
                          startIcon={<PlayArrowOutlined />}
                          disabled={simulate.isPending}
                          onClick={() => {
                            setSelected(request);
                            setResult(null);
                            simulate.mutate(request.id);
                          }}>
                          {t("putaway.actions.simulate")}
                        </Button>
                      )}
                      status={
                        <Chip label={operationalStatusLabel(request.status)}
                          color={request.status === "EXCEPTION"
                            ? "error" : "warning"} />
                      } />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {simulate.isError && (
            <Alert severity="error">
              {simulate.error instanceof Error
                ? simulate.error.message : t("putaway.errors.simulation")}
            </Alert>
          )}

          {accept.isError && (
            <Alert severity="error">
              {accept.error instanceof Error
                ? accept.error.message : t("putaway.errors.taskCreate")}
            </Alert>
          )}
        </>
      )}

      <Dialog open={result !== null}
        data-testid="putaway-simulation-dialog"
        onClose={() => {
          setResult(null);
          setSelected(null);
        }}
        fullWidth maxWidth="lg">
        {result && (
          <>
            <DialogTitle>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                alignItems={{ sm: "center" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={800}>
                    {result.status === "PROPOSED"
                      ? t("putaway.labels.proposedLocation", { location: result.selectedBinCode })
                      : t("putaway.labels.noLocation")}
                  </Typography>
                  {selected && (
                    <Stack direction="row" useFlexGap flexWrap="wrap"
                      spacing={1} sx={{ mt: .75 }}>
                      <Chip size="small" variant="outlined"
                        label={`${selected.huNumber} · ${selected.itemCode}`} />
                      <Chip size="small" variant="outlined"
                        label={`${selected.quantity} ${selected.uom}`} />
                      {selected.clientName && (
                        <Chip size="small" variant="outlined"
                          label={`${t("common.client")} ${selected.clientName}${selected.clientCode
                            ? ` (${selected.clientCode})` : ""}`} />
                      )}
                      <Chip size="small" variant="outlined"
                        label={t("putaway.labels.departure", { source: selected.sourcePointCode })} />
                    </Stack>
                  )}
                  {diagnosticAlert?.clientCode && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mt: .5 }}>
                      {t("putaway.labels.owner", { owner: diagnosticAlert.clientCode })}
                      {" · "}{diagnosticAlert.clientName}
                      {diagnosticAlert.packagingCode
                        ? ` · ${t("putaway.fields.packaging")} : ${diagnosticAlert.packagingCode}`
                        : ""}
                    </Typography>
                  )}
                </Box>
                <Chip
                  icon={result.status === "PROPOSED"
                    ? <CheckCircleOutline /> : <ErrorOutline />}
                  label={operationalStatusLabel(result.status)}
                  color={result.status === "PROPOSED" ? "success" : "error"} />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography color="text.secondary">
                  {result.explanation}
                  {result.selectedRuleCode
                    ? ` ${t("putaway.labels.retainedRule", { rule: result.selectedRuleCode })}` : ""}
                  {result.requiredEquipmentTypeCode
                    ? ` ${t("putaway.labels.requiredEquipment", { equipment: result.requiredEquipmentTypeCode })}.` : ""}
                </Typography>
                <Typography variant="h6">{t("putaway.labels.evaluationTrace")}</Typography>
                {result.candidates.map((candidate, index) => (
                  <Card key={`${candidate.ruleCode}-${candidate.binCode}-${index}`}
                    variant="outlined">
                    <CardContent>
                      <Stack spacing={0.75}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                          alignItems={{ sm: "center" }}>
                          <Typography fontWeight={700} sx={{ flexGrow: 1 }}>
                            P{candidate.priority} · {candidate.ruleName}
                            {" → "}{candidate.binCode} · {candidate.areaCode}
                          </Typography>
                          <Chip size="small"
                            label={candidate.selected
                              ? t("putaway.labels.selected")
                              : candidate.eligible ? t("putaway.labels.compatible") : t("putaway.labels.rejected")}
                            color={candidate.selected
                              ? "success" : candidate.eligible ? "info" : "error"} />
                        </Stack>
                        {candidate.orientation && (
                          <Typography variant="body2">
                            Orientation : {candidate.orientation}
                          </Typography>
                        )}
                        {candidate.rejectionReasons.length > 0 && (
                          <Typography variant="body2" color="error">
                            Rejets : {candidate.rejectionReasons.join(", ")}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {t("putaway.labels.acceptedChecks", { checks: candidate.acceptedChecks.join(", ") || t("putaway.labels.none") })}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setResult(null);
                setSelected(null);
              }}>
                {t("common.actions.close")}
              </Button>
              {result.status === "PROPOSED" && (
                <Button variant="contained" color="success"
                  data-testid="putaway-accept-proposal"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate(result)}>
                  {t("putaway.actions.acceptProposal")}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {tab === "tasks" && (
        <>
          {createdTask && (
            <Alert severity="success">
              {t("putaway.labels.createdTask", { hu: createdTask.huNumber,
                destination: createdTask.destinationBinCode })}
            </Alert>
          )}
          {tasks.isLoading && <CircularProgress />}
          {tasks.isError && (
            <Alert severity="error">
              {t("putaway.errors.tasks")}
            </Alert>
          )}
          {tasks.data?.length === 0 && (
            <Alert severity="info">{t("putaway.empty.tasks")}</Alert>
          )}
          <Stack spacing={1.5}>
            {transfers.data?.filter((task) =>
              task.status === "IN_PROGRESS").map((task) => (
              <Card key={task.id} id={`putaway-${task.id}`} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "center" }}>
                    <AssignmentOutlined color="secondary" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {task.huNumber} → {task.destinationPointCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {transferLabel(task, t)}
                        {` · ${t("putaway.labels.source", { source: task.sourcePointCode })}`}
                      </Typography>
                    </Box>
                    <ListRowActions
                      actions={
                        <Button variant="contained"
                          data-testid={`transfer-complete-${task.id}`}
                          disabled={transferAction.isPending}
                          onClick={() => transferAction.mutate(task)}>
                          {t("putaway.actions.confirmDeposit")}
                        </Button>
                      }
                      status={<Chip label={operationalStatusLabel(task.status)}
                        color={operationalStatusColor(task.status)} />}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {activeTasks.map((task) => {
              const active = ["CREATED", "ASSIGNED", "IN_PROGRESS"]
                .includes(task.status);
              return (
                <Card key={task.id} id={`putaway-${task.id}`} variant="outlined"
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest(
                      "button,a,[role='button']")) return;
                    setMobileSelection({ kind: "task", id: task.id });
                  }}
                  sx={{ cursor: { xs: "pointer", sm: "default" },
                    outline: { xs: mobileSelection?.kind === "task"
                      && mobileSelection.id === task.id
                      ? "2px solid" : "none", sm: "none" },
                    outlineColor: "primary.main" }}>
                  <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                      alignItems={{ md: "center" }}>
                      <AssignmentOutlined color={active ? "primary" : "disabled"} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography fontWeight={800}>
                          {task.huNumber} → {task.destinationBinCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("putaway.labels.departure", { source: task.sourcePointCode })}
                          {task.clientName
                            ? ` · ${t("common.client")} ${task.clientName}${task.clientCode
                              ? ` (${task.clientCode})` : ""}`
                            : ""}
                          {` · ${t("putaway.labels.priority", { priority: task.priority })}`}
                          {` · ${t("putaway.labels.requiredEquipment", { equipment: task.requiredEquipmentTypeCode })}`}
                          {task.assignedEquipmentNumber
                            ? ` · ${t("putaway.labels.assignedEquipment", { equipment: task.assignedEquipmentNumber })}` : ""}
                        </Typography>
                        <SourceTrace sources={[
                          task.asnNumber, task.receiptNumber, task.huNumber,
                          task.qualityInspectionNumber
                        ]} step={`Put-away ${task.destinationBinCode}`}
                          client={task.clientName ?? task.clientCode} />
                        <Stack direction="row" spacing={0.75} alignItems="center"
                          sx={{ mt: 0.75 }}>
                          <AccessTimeOutlined fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {task.reservationExpiresAt
                              ? t("putaway.labels.reservationExpires", { date: new Date(task.reservationExpiresAt).toLocaleString(locale) })
                              : t("putaway.labels.reservationNoExpiry")}
                          </Typography>
                        </Stack>
                      </Box>
                      <ListRowActions
                        actions={!["COMPLETED", "CANCELLED"]
                          .includes(task.status) ? (
                            <Button variant="contained"
                              sx={{ display: { xs: "none", sm: "inline-flex" } }}
                              data-testid={`putaway-open-task-${task.huNumber}`}
                              onClick={() => setExecutionTask(task)}>
                              {task.status === "IN_PROGRESS"
                                ? t("putaway.actions.confirmDeposit")
                                : task.status === "ASSIGNED"
                                  ? t("putaway.actions.confirmClaim")
                                  : t("putaway.actions.claim")}
                            </Button>
                          ) : undefined}
                        status={
                          <Chip label={operationalStatusLabel(task.status)}
                            color={operationalStatusColor(task.status)} />
                        } />
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </>
      )}
      {tab === "alerts" && (
        <>
          {alerts.isLoading && <CircularProgress />}
          {alerts.isError && (
            <Alert severity="error">
              {t("putaway.errors.alerts")}
            </Alert>
          )}
          {activeAlerts.length === 0
            && repackagingAlerts.length === 0
            && !transfers.data?.some((task) => task.status === "EXCEPTION") && (
            <Alert severity="info">{t("putaway.empty.alerts")}</Alert>
          )}
          <Stack spacing={1.5}>
            {repackagingAlerts.map((task: RepackagingTask) => (
              <Card key={task.id} id={`putaway-${task.id}`} variant="outlined"
                sx={{ borderLeft: 4, borderLeftColor: "error.main" }}>
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "center" }}>
                    <WarningAmberOutlined color="error" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {task.huNumber} · Exception de reconditionnement
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.itemCode} · {task.warehouseCode}
                        {` · ${t("putaway.labels.transferStation", { station: task.processPointCode })}`}
                      </Typography>
                      <Typography sx={{ mt: 0.75 }}>
                        {task.exceptionCode?.replaceAll("_", " ")}
                        {" — "}{task.exceptionComment}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.exceptionReportedAt
                          ? new Date(task.exceptionReportedAt)
                            .toLocaleString(locale) : ""}
                        {" · "}{task.exceptionReportedByName
                          ?? t("putaway.labels.unknownUser")}
                      </Typography>
                    </Box>
                    <ListRowActions actions={
                      <Button variant="contained"
                        onClick={() => onOpenRepackaging(task.id)}>
                        Traiter
                      </Button>
                    } status={<Chip label={t("putaway.labels.toProcess")} color="error" />} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {transfers.data?.filter((task) => task.status === "EXCEPTION")
              .map((task) => (
                <Card key={task.id} id={`putaway-${task.id}`} variant="outlined"
                  sx={{ borderLeft: 4, borderLeftColor: "error.main" }}>
                  <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                      alignItems={{ md: "center" }}>
                      <WarningAmberOutlined color="error" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography fontWeight={800}>
                          {task.huNumber} · {transferLabel(task, t)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {task.sourcePointCode}
                          {" → "}{task.destinationPointCode}
                        </Typography>
                        <Typography sx={{ mt: 0.75 }}>
                          {task.exceptionCode?.replaceAll("_", " ")}
                          {" — "}{task.exceptionComment}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.exceptionReportedAt
                            ? new Date(task.exceptionReportedAt)
                              .toLocaleString(locale) : ""}
                          {" · "}{task.exceptionReportedByName
                            ?? t("putaway.labels.unknownUser")}
                        </Typography>
                      </Box>
                      <ListRowActions actions={
                        <Button variant="contained"
                          onClick={() => {
                            setSelectedTransfer(task);
                            setTransferExceptionMode("resolve");
                            setTransferExceptionAction("RETRY");
                            setTransferExceptionComment("");
                            setTransferDestinationId("");
                          }}>
                          Traiter
                        </Button>
                      } status={<Chip label={t("putaway.labels.toProcess")} color="error" />} />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            {activeAlerts.map((alert) => (
              <Card key={alert.id} id={`putaway-${alert.id}`} variant="outlined"
                sx={{ borderLeft: 4, borderLeftColor: alert.status === "OPEN"
                  ? "error.main" : "warning.main" }}>
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "flex-start" }}>
                    <WarningAmberOutlined color={alert.status === "OPEN"
                      ? "error" : "warning"} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {alert.huNumber} ·{" "}
                        {reasonLabel(t, alert.reasonCode)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {alert.rejectedBinCode
                          ? t("putaway.labels.rejectedLocation", { location: alert.rejectedBinCode })
                          : t("putaway.labels.preProposalException")}
                        {alert.replacementBinCode
                          ? ` · ${t("putaway.labels.replacementLocation", { location: alert.replacementBinCode })}`
                          : alert.complementaryTaskType === "REPACKAGING"
                            ? ` · ${t("putaway.labels.routedRepackaging")}`
                            : alert.complementaryTaskType === "QUALITY_INSPECTION"
                              ? ` · ${t("putaway.labels.routedQuality")}`
                              : ` · ${t("putaway.labels.noAlternative")}`}
                      </Typography>
                      <Stack direction="row" useFlexGap flexWrap="wrap"
                        spacing={1} sx={{ mt: 1 }}>
                        <Chip size="small" variant="outlined"
                          label={`${alert.itemCode} · ${alert.itemName}`} />
                        <Chip size="small" variant="outlined"
                          label={`${alert.quantity} ${alert.uom}`} />
                        <Chip size="small" variant="outlined"
                          label={alert.clientCode
                            ? `${alert.clientCode} · ${alert.clientName}`
                            : t("putaway.labels.unknownOwner")}
                          color={alert.clientCode ? "default" : "warning"} />
                        <Chip size="small" variant="outlined"
                          label={t("putaway.labels.departure", { source: alert.sourcePointCode })} />
                        {alert.packagingCode && (
                          <Chip size="small" variant="outlined"
                            label={`${alert.packagingCode} · ${alert.packagingName}`} />
                        )}
                      </Stack>
                      {alert.complementaryTaskType && (
                        <Typography variant="body2" color="text.secondary">
                          {t("putaway.labels.complementaryTask", { type: alert.complementaryTaskType })}
                          {" · "}{alert.complementaryTaskStatus}
                        </Typography>
                      )}
                      <Typography sx={{ mt: 1 }}>{alert.comment}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(alert.reportedAt).toLocaleString(locale)}
                        {" · "}{alert.reportedBy ?? alert.reportedByEmail
                          ?? t("putaway.labels.unknownUser")}
                      </Typography>
                    </Box>
                    <ListRowActions
                      actions={
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          {alert.decisionId && (
                            <Button variant="outlined"
                              data-testid={`putaway-diagnostic-${alert.huNumber}`}
                              disabled={loadDiagnostic.isPending}
                              onClick={() =>
                                loadDiagnostic.mutate(alert.decisionId!)}>
                              {t("putaway.actions.viewDiagnostic")}
                            </Button>
                          )}
                          {["RESIMULATE", "REPLAN"].includes(
                            alert.resolutionAction
                          )
                            && alert.status !== "RESOLVED" && (
                              <Button variant="contained"
                                data-testid={`putaway-resimulate-${alert.huNumber}`}
                                startIcon={<PlayArrowOutlined />}
                                disabled={resimulateAlert.isPending}
                                onClick={() => {
                                  setResimulationAlert(alert);
                                  setReleaseRejectedBin(false);
                                  setResimulationComment("");
                                }}>
                                {t("putaway.actions.resimulate")}
                              </Button>
                            )}
                          {alert.resolutionAction
                            === "PACKAGING_IDENTIFICATION"
                            && alert.status !== "RESOLVED" && (
                              <Button variant="contained"
                                onClick={() => {
                                  setPackagingAlert(alert);
                                  setPackagingId("");
                                  setPackagingComment("");
                                }}>
                                {t("putaway.actions.identifyPackaging")}
                              </Button>
                            )}
                        </Stack>
                      }
                      status={
                        <Chip label={alert.replacementTaskId
                          ? t("putaway.labels.replanned")
                          : alert.complementaryTaskType === "REPACKAGING"
                            ? t("putaway.labels.repackaging")
                            : alert.complementaryTaskType === "QUALITY_INSPECTION"
                              ? t("putaway.labels.qualityControl")
                              : alert.status === "ASSIGNED"
                                ? t("putaway.labels.assigned") : t("putaway.labels.toProcess")}
                          color={alert.status === "OPEN"
                            ? "error" : "warning"} />
                      } />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}
      {tab === "closed" && (
        <>
          {tasks.isLoading && <CircularProgress />}
          {tasks.isError && (
            <Alert severity="error">
              {t("putaway.errors.closed")}
            </Alert>
          )}
          {closedTours.length === 0 && closedStandaloneTasks.length === 0 && (
            <Alert severity="info">
              {t("putaway.empty.closedFiltered")}
            </Alert>
          )}
          <Stack spacing={1.5}>
            {closedTours.map((tour) => {
              const completedSteps = tour.steps.filter((step) =>
                step.taskStatus === "COMPLETED").length;
              const cancelledSteps = tour.steps.filter((step) =>
                step.taskStatus === "CANCELLED").length;
              return (
                <Card key={tour.id} id={`putaway-${tour.id}`} variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
                      alignItems={{ md: "center" }}>
                      <Stack direction={{ xs: "column", md: "row" }}
                        spacing={1.5} sx={{ flex: 1, minWidth: 0,
                          width: { xs: "100%", md: "auto" } }}>
                        <AssignmentOutlined color={tour.status === "CANCELLED"
                          ? "disabled" : "secondary"} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={850}>
                            {tour.number} · {workQueueLabel(tour.workQueueCode, tour.workQueueName)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {tour.steps.length} HU
                            {tour.equipmentNumber
                              ? ` · Engin ${tour.equipmentNumber}` : ""}
                            {` · ${t("putaway.labels.stocked", { count: completedSteps })}`}
                            {cancelledSteps > 0
                              ? ` · ${t("putaway.labels.cancelled", { count: cancelledSteps })}`
                              : ""}
                            {tour.completedAt
                              ? ` · ${t("putaway.labels.closedOn", { date: new Date(tour.completedAt)
                                .toLocaleString(locale) })}` : ""}
                          </Typography>
                        </Box>
                      </Stack>
                      <ListRowActions status={
                        <Chip label={operationalStatusLabel(tour.status)}
                          color={operationalStatusColor(tour.status)} />
                      } />
                      <IconButton className="mobile-card-expand"
                        aria-label={t("putaway.labels.detailsClosedMission")}
                        onClick={() => setExpandedTourId((current) =>
                          current === tour.id ? null : tour.id)}
                        sx={{ alignSelf: { xs: "center", md: "auto" },
                          width: 40, height: 40,
                          transform: expandedTourId === tour.id
                            ? "rotate(180deg)" : "none",
                          transition: "transform .2s" }}>
                        <ExpandMore />
                      </IconButton>
                    </Stack>
                    <Collapse in={expandedTourId === tour.id}>
                      <Stack spacing={.75} sx={{ mt: 1.5, pt: 1.5,
                        borderTop: 1, borderColor: "divider" }}>
                        {tour.steps.map((step) => (
                          <Box key={step.id} sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "28px minmax(0, 1fr) auto",
                              md: "36px minmax(150px, 1fr) minmax(140px, 1fr) auto"
                            },
                            gap: 1, alignItems: "center", py: .5
                          }}>
                            <Typography fontWeight={800}>{step.sequence}.</Typography>
                            <Typography fontWeight={750}>{step.huNumber}</Typography>
                            <Typography sx={{
                              gridColumn: { xs: "2 / 4", md: "auto" },
                              gridRow: { xs: 2, md: "auto" }
                            }}>
                              {step.destinationBinCode}
                            </Typography>
                            <Chip size="small"
                              sx={{ gridColumn: { xs: 3, md: "auto" },
                                gridRow: { xs: 1, md: "auto" },
                                justifySelf: { xs: "end", md: "auto" } }}
                              label={operationalStatusLabel(step.taskStatus)}
                              color={operationalStatusColor(step.taskStatus)} />
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
            {transfers.data?.filter((task) =>
              ["COMPLETED", "CANCELLED"].includes(task.status)
            ).map((task) => (
              <Card key={task.id} id={`putaway-${task.id}`} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "center" }}>
                    <CheckCircleOutline color="success" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {task.huNumber} → {task.destinationPointCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {transferLabel(task, t)}
                      </Typography>
                    </Box>
                    <ListRowActions status={
                      <Chip label={operationalStatusLabel(task.status)}
                        color={operationalStatusColor(task.status)} />
                    } />
                    <IconButton className="mobile-card-expand"
                      aria-label={t("putaway.labels.detailsTransfer")}
                      onClick={() => setExpandedClosedItemId((current) =>
                        current === task.id ? null : task.id)}
                      sx={{ transform: expandedClosedItemId === task.id
                        ? "rotate(180deg)" : "none" }}>
                      <ExpandMore />
                    </IconButton>
                  </Stack>
                  <Collapse in={expandedClosedItemId === task.id}>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                      <Typography variant="body2">
                        {task.sourcePointCode} → {task.destinationPointCode}
                        {task.completedAt ? ` · ${t("putaway.labels.completedOn", { date: new Date(task.completedAt).toLocaleString(locale) })}` : ""}
                      </Typography>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
            {closedStandaloneTasks.map((task) => (
              <Card key={task.id} id={`putaway-${task.id}`} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "center" }}>
                    <CheckCircleOutline color={task.status === "COMPLETED"
                      ? "success" : "disabled"} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {task.huNumber} → {task.destinationBinCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("putaway.labels.departure", { source: task.sourcePointCode })}
                        {task.tourNumber ? ` · mission ${task.tourNumber}` : ""}
                        {` · ${t("putaway.labels.requiredEquipment", { equipment: task.requiredEquipmentTypeCode })}`}
                        {task.completedAt
                          ? ` · ${t("putaway.labels.closedOn", { date: new Date(task.completedAt)
                            .toLocaleString(locale) })}` : ""}
                      </Typography>
                    </Box>
                    <PutawayMissionDocumentLink task={task} />
                    {task.tourNumber && (
                      <Chip label={`Mission ${task.tourNumber}`}
                        sx={{ alignSelf: { xs: "flex-start", md: "auto" } }}
                        color="primary" variant="outlined" />
                    )}
                    <ListRowActions status={
                      <Chip label={operationalStatusLabel(task.status)}
                        color={operationalStatusColor(task.status)} />
                    } />
                    <IconButton className="mobile-card-expand"
                      aria-label={t("putaway.labels.detailsPutaway")}
                      onClick={() => setExpandedClosedItemId((current) =>
                        current === task.id ? null : task.id)}
                      sx={{ transform: expandedClosedItemId === task.id
                        ? "rotate(180deg)" : "none" }}>
                      <ExpandMore />
                    </IconButton>
                  </Stack>
                  <Collapse in={expandedClosedItemId === task.id}>
                    <Stack spacing={1} sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                      <SourceTrace sources={[
                        task.asnNumber, task.receiptNumber, task.huNumber,
                        task.qualityInspectionNumber, task.tourNumber
                      ]} step={t("putaway.labels.stockedAt", { location: task.destinationBinCode })}
                        client={task.clientName ?? task.clientCode} />
                      <Typography variant="body2" color="text.secondary"
                        sx={{ display: { xs: "none", sm: "block" } }}>
                        {t("putaway.labels.requiredEquipment", { equipment: task.requiredEquipmentTypeCode })}
                        {task.assignedEquipmentNumber ? ` · ${t("putaway.labels.usedEquipment", { equipment: task.assignedEquipmentNumber })}` : ""}
                        {task.huScannedAt ? ` · ${t("putaway.labels.huScannedAt", { date: new Date(task.huScannedAt).toLocaleString(locale) })}` : ""}
                        {task.destinationScannedAt ? ` · ${t("putaway.labels.locationScannedAt", { date: new Date(task.destinationScannedAt).toLocaleString(locale) })}` : ""}
                      </Typography>
                      <Stack spacing={0.25} sx={{ display: { xs: "flex", sm: "none" } }}>
                        <Typography variant="body2" color="text.secondary">
                          {t("putaway.labels.requiredEquipment", { equipment: task.requiredEquipmentTypeCode })}
                          {task.assignedEquipmentNumber ? ` · ${t("putaway.labels.usedEquipment", { equipment: task.assignedEquipmentNumber })}` : ""}
                        </Typography>
                        {task.huScannedAt && (
                          <Typography variant="body2" color="text.secondary">
                            {t("putaway.labels.huScannedAt", { date: new Date(task.huScannedAt).toLocaleString(locale) })}
                          </Typography>
                        )}
                        {task.destinationScannedAt && (
                          <Typography variant="body2" color="text.secondary">
                            {t("putaway.labels.locationScannedAt", { date: new Date(task.destinationScannedAt).toLocaleString(locale) })}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}
      </Box>
      <MobileActionBar
        secondaryIcon={selectedMobileQueue ? <PlayArrowOutlined /> : undefined}
        secondaryLabel={t("putaway.actions.directMission")}
        secondaryDisplayLabel="Directe"
        onSecondary={selectedMobileQueue && selectedQueueTaskIds.length === 1
          ? () => {
            const candidate = (queueCandidates.data ?? []).find((entry) =>
              entry.taskId === selectedQueueTaskIds[0]);
            if (candidate) void startDirectMission(selectedMobileQueue.id, candidate);
          }
          : undefined}
        primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary}
        onMore={selectedMobileMissionDocumentTask || selectedMobileTourCanCancel
          ? setMobileMenuAnchor : undefined} />
      <Menu anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)}
        onClose={() => setMobileMenuAnchor(null)}>
        {selectedMobileMissionDocumentTask && (
          <MenuItem onClick={() => {
            setMobileMenuAnchor(null);
            void openPutawayMissionDocument(selectedMobileMissionDocumentTask)
              .catch(() => window.alert("Ouverture du document de mission impossible."));
          }}>
            <DescriptionOutlined sx={{ mr: 1 }} /> Document mission
          </MenuItem>
        )}
        {selectedMobileTourCanCancel && selectedMobileTour && (
          <MenuItem sx={{ color: "error.main" }} onClick={() => {
            setMobileMenuAnchor(null);
            if (window.confirm(`Annuler la mission ${selectedMobileTour.number} ? Les HU retourneront dans leur file.`)) {
              cancelTour.mutate(selectedMobileTour);
            }
          }}>
            Annuler la mission
          </MenuItem>
        )}
      </Menu>
      <Dialog open={executionTour !== null} fullWidth maxWidth={false}
        hideBackdrop disableEnforceFocus
        onClose={() => setExecutionTourId(null)}
        sx={{
          pointerEvents: "none",
          "& .MuiDialog-container": {
            alignItems: { xs: "stretch", md: "flex-start" },
            ml: { md: "64px" },
            pt: { md: "80px" }
          },
          "& .MuiDialog-paper": {
            pointerEvents: "auto",
            width: { xs: "100%", md: "calc(100% - 160px)" },
            maxWidth: { md: 1600 },
            height: { xs: "100%", md: "calc(100dvh - 96px)" },
            maxHeight: { xs: "100%", md: "calc(100dvh - 96px)" },
            m: { xs: 0, md: 1 }
          }
        }}>
        <DialogTitle>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}
            alignItems={{ md: "center" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={850}>
                Mission de mise en stock · {executionTour?.number}
              </Typography>
              {executionTour && (
                <Typography color="text.secondary">
                  {executionTour.equipmentNumber
                    ? t("putaway.labels.equipment", { number: executionTour.equipmentNumber }) : t("putaway.actions.directMission")}
                  {` · ${t("putaway.labels.depositedCount", { completed: executionTour.steps.filter((step) =>
                    step.taskStatus === "COMPLETED").length, total: executionTour.steps.length })}`}
                  {` · ${t("putaway.labels.recommendedDistance", { distance: executionTour.estimatedDistanceUnits })}`}
                </Typography>
              )}
            </Box>
            {executionTour && (
              <Chip label={operationalStatusLabel(executionTour.status)}
                color={operationalStatusColor(executionTour.status)} />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {executionTour && (() => {
            const recommended = executionTour.steps.find((step) =>
              !["COMPLETED", "CANCELLED"].includes(step.taskStatus));
            return (
              <Stack spacing={2}>
                <Alert severity="info">
                  {t("putaway.help.recommendedSequence")}
                </Alert>
                {executionTour.steps.map((step) => {
                  const task = tasks.data?.find((entry) => entry.id === step.taskId);
                  const completed = step.taskStatus === "COMPLETED";
                  const closed = completed || step.taskStatus === "CANCELLED";
                  const isRecommended = recommended?.id === step.id;
                  return (
                    <Card key={step.id} variant="outlined"
                      data-testid={`putaway-tour-step-${step.huNumber}`} sx={{
                      borderLeft: 4,
                      borderLeftColor: completed ? "success.main"
                        : isRecommended ? "primary.main" : "divider"
                    }}>
                      <CardContent>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                          alignItems={{ md: "center" }}>
                          <Typography variant="h6" fontWeight={900}
                            sx={{ minWidth: 44 }}>
                            {step.sequence}.
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={850}>{step.huNumber}</Typography>
                            <Typography color="text.secondary">
                              Destination {step.destinationBinCode}
                              {` · distance ${step.distanceFromPrevious} u`}
                            </Typography>
                          </Box>
                          {isRecommended && !closed && (
                            <Chip label={t("putaway.labels.recommendedStep")} color="primary"
                              variant="outlined" />
                          )}
                          {!closed && task && (
                            <Button variant={isRecommended ? "contained" : "outlined"}
                              data-testid={`putaway-start-tour-step-${step.huNumber}`}
                              disabled={startTourStep.isPending}
                              onClick={() => startTourStep.mutate(task)}>
                              {task.status === "CREATED" ? t("putaway.actions.openLocation")
                                : t("putaway.actions.continueStep")}
                            </Button>
                          )}
                          <Chip label={operationalStatusLabel(step.taskStatus)}
                            color={operationalStatusColor(step.taskStatus)} />
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                {startTourStep.isError && (
                  <Alert severity="error">
                    {startTourStep.error instanceof Error
                      ? startTourStep.error.message
                      : t("putaway.errors.stepOpen")}
                  </Alert>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExecutionTourId(null)}>
            {executionTour?.steps.every((step) =>
              ["COMPLETED", "CANCELLED"].includes(step.taskStatus))
              ? t("putaway.actions.finalize") : t("common.actions.close")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={directEquipment !== null} fullWidth maxWidth="sm"
        onClose={() => setDirectEquipment(null)}>
        <DialogTitle>{t("putaway.fields.handlingEquipment")}</DialogTitle>
        <DialogContent dividers>
          {directEquipment && (
            <Stack spacing={2}>
              <Alert severity="info">
                {directEquipment.candidate.huNumber} · {weightLabel(
                  directEquipment.selection.weightKg)} · {directEquipment.selection
                  .palletized ? t("putaway.labels.palletized")
                    : t("putaway.labels.notPalletized")}
              </Alert>
              <ScanInput
                disabled={directNoEquipment}
                options={directEquipment.selection.equipment.map((item) => ({
                  value: item.equipmentNumber,
                  label: `${item.equipmentNumber} · ${item.typeName}`
                    + (item.maxLoadKg !== null
                      ? ` · ${t("putaway.labels.capacity", {
                        weight: weightLabel(item.maxLoadKg)
                      })}`
                      : "")
                }))}
                value={directEquipmentIdentifier}
                onChange={(value) => {
                  setDirectEquipmentIdentifier(value);
                  const selected = directEquipment.selection.equipment.find((item) =>
                    item.equipmentNumber.toUpperCase() === value.trim().toUpperCase());
                  setDirectEquipmentId(selected?.id ?? "");
                }}
                required={!directNoEquipment}
                label={t("putaway.fields.handlingEquipment")}
                helperText={t("putaway.help.equipmentChoice")}
              />
              <FormControlLabel control={<Checkbox
                checked={directNoEquipment}
                onChange={(event) => {
                  setDirectNoEquipment(event.target.checked);
                  if (event.target.checked) {
                    setDirectEquipmentId("");
                    setDirectEquipmentIdentifier("");
                  }
                }} />}
                label={t("putaway.fields.noEquipment")} />
              {directEquipment.selection.equipment.length === 0 && (
                <Alert severity="info">
                  Aucun moyen compatible n’est actuellement disponible dans la liste.
                  Vous pouvez scanner un moyen redevenu disponible ou poursuivre sans
                  engin.
                </Alert>
              )}
              {createTour.isError && (
                <Alert severity="error">
                  {createTour.error instanceof Error ? createTour.error.message
                    : t("putaway.errors.missionCreate")}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDirectEquipment(null)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained" disabled={createTour.isPending
            || (!directNoEquipment && !directEquipmentIdentifier.trim())}
            onClick={() => directEquipment && createTour.mutate({
              queueId: directEquipment.queueId,
              taskIds: [directEquipment.candidate.taskId], direct: true,
              handlingEquipmentId: directNoEquipment
                ? null : directEquipmentId || null,
              handlingEquipmentIdentifier: directNoEquipment
                ? null : directEquipmentIdentifier.trim(),
              scanEquipment: false
            })}>
            {t("putaway.actions.createMission")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={loadingTour !== null} fullWidth maxWidth="md"
        onClose={() => setLoadingTour(null)}>
        <DialogTitle>Chargement de l’engin · {loadingTour?.number}</DialogTitle>
        <DialogContent dividers>
          {loadingTour && (
            <Stack spacing={2}>
              <Alert severity={loadingTour.equipmentScanned ? "info" : "warning"}>
                {loadingTour.equipmentScanned
                  ? t("putaway.labels.equipmentIdentified", { number: loadingTour.equipmentNumber })
                  : t("putaway.help.equipmentChoice")}
              </Alert>
              {loadingTour.steps.map((step) => {
                const skipped = skippedTourStepIds.includes(step.id);
                return (
                  <Box key={step.id}>
                    <Stack sx={{ display: { xs: "flex", sm: "none" } }}
                      spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={750}>{step.huNumber}</Typography>
                          <Typography color="text.secondary">
                            {step.destinationBinCode}
                          </Typography>
                        </Box>
                        <Chip size="small"
                          label={step.loaded ? t("putaway.labels.scanned") : skipped ? t("putaway.labels.excluded") : t("putaway.labels.toScan")}
                          color={step.loaded ? "success" : skipped ? "default" : "warning"} />
                      </Stack>
                      {!step.loaded && (
                        <Button fullWidth variant="outlined" size="small"
                          color={skipped ? "primary" : "inherit"}
                          onClick={() => setSkippedTourStepIds((current) =>
                            skipped ? current.filter((id) => id !== step.id)
                              : [...current, step.id])}>
                          {skipped ? t("putaway.actions.reinstate") : t("putaway.actions.exclude")}
                        </Button>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center"
                      sx={{ display: { xs: "none", sm: "flex" } }}>
                      <Typography sx={{ flex: 1 }} fontWeight={750}>
                        {step.huNumber}
                      </Typography>
                      <Typography color="text.secondary">
                        {step.destinationBinCode}
                      </Typography>
                      <Chip size="small"
                        label={step.loaded ? t("putaway.labels.scanned") : skipped ? t("putaway.labels.excluded") : t("putaway.labels.toScan")}
                        color={step.loaded ? "success" : skipped ? "default" : "warning"} />
                      {!step.loaded && (
                        <Button size="small" color={skipped ? "primary" : "inherit"}
                          onClick={() => setSkippedTourStepIds((current) =>
                            skipped ? current.filter((id) => id !== step.id)
                              : [...current, step.id])}>
                          {skipped ? t("putaway.actions.reinstate") : t("putaway.actions.exclude")}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                );
              })}
              <Typography fontWeight={800}>
                {t("putaway.labels.loadedCount", { loaded: loadingTour.steps.filter((step) => step.loaded).length,
                  total: loadingTour.steps.length })}
              </Typography>
              {loadingTour.equipmentScanned ? (
                <ScanInput autoFocus label={t("putaway.fields.huCode")} value={tourScan}
                  onChange={setTourScan}
                  onSubmit={() => scanTour.mutateAsync()}
                  submitting={scanTour.isPending}
                  submitLabel={t("putaway.actions.scanHu")} />
              ) : (
                <ScanInput
                  options={(tourEquipment.data ?? []).map((item) => ({
                    value: item.equipmentNumber,
                    label: `${item.equipmentNumber} · ${item.typeName}${
                      item.maxLoadKg == null ? ""
                        : ` · ${t("putaway.labels.capacity", { weight: item.maxLoadKg.toLocaleString(locale) })}`
                    }`
                  }))}
                  value={tourScan} onChange={setTourScan}
                  onSubmit={() => scanTour.mutateAsync()}
                  submitting={scanTour.isPending}
                  submitLabel={t("putaway.actions.validate")}
                  autoFocus label={t("putaway.fields.compatibleEquipment")}
                  helperText={t("putaway.help.equipmentList")}
                />
              )}
              {!loadingTour.equipmentScanned && tourEquipment.isError && (
                <Alert severity="error">{t("putaway.errors.eligibleEquipment")}</Alert>
              )}
              {!loadingTour.equipmentScanned && !tourEquipment.isLoading
                && (tourEquipment.data?.length ?? 0) === 0 && (
                <Alert severity="warning">{t("putaway.errors.noEquipment")}</Alert>
              )}
              {scanTour.isError && (
                <Alert severity="error">
                  {scanTour.error instanceof Error
                    ? scanTour.error.message : "Scan non reconnu."}
                </Alert>
              )}
              {completeTourLoading.isError && (
                <Alert severity="error">
                  {completeTourLoading.error instanceof Error
                    ? completeTourLoading.error.message
                    : t("putaway.errors.loading")}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{
          px: 2, py: 1.5, justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 1
        }}>
          <Stack direction="row" spacing={.5}>
            <Button sx={{ whiteSpace: "nowrap" }}
              onClick={() => setLoadingTour(null)}>{t("common.actions.close")}</Button>
            {loadingTour && (
              <Button color="error" sx={{ whiteSpace: "nowrap" }}
                disabled={cancelTour.isPending}
                onClick={() => {
                  if (window.confirm(`Annuler la mission ${loadingTour.number} ? Les HU retourneront dans leur file.`)) {
                    cancelTour.mutate(loadingTour);
                  }
                }}>
                Annuler
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
            {loadingTour?.equipmentScanned && (
              <Button variant="contained" color="success"
                sx={{ whiteSpace: "nowrap" }}
                disabled={completeTourLoading.isPending
                  || loadingTour.steps.every((step) => !step.loaded)
                  || loadingTour.steps.some((step) =>
                    !step.loaded && !skippedTourStepIds.includes(step.id))}
                onClick={() => completeTourLoading.mutate()}>
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                  Terminer
                </Box>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Terminer le chargement
                </Box>
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
      <PutawayTaskDialog task={executionTask}
        tourMode={Boolean(executionTourId)}
        onClose={() => setExecutionTask(null)}
        onChanged={async (updated) => {
          setExecutionTask(executionTourId && updated.status === "COMPLETED"
            ? null : updated);
          await queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
          await queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
          await queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
          await queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
        }}
        onRefresh={() => {
          void queryClient.invalidateQueries({ queryKey: ["putaway-tasks"] });
          void queryClient.invalidateQueries({ queryKey: ["putaway-requests"] });
          void queryClient.invalidateQueries({ queryKey: ["putaway-alerts"] });
          void queryClient.invalidateQueries({ queryKey: ["putaway-tours"] });
          void queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
        }} />
      <Dialog open={selectedTransfer !== null}
        onClose={() => {
          setSelectedTransfer(null);
          setTransferExceptionMode(null);
        }}
        fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedTransfer ? transferLabel(selectedTransfer, t) : t("putaway.transfers.generic")}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransfer && (
            <Stack spacing={2}>
              <Typography fontWeight={800}>
                {selectedTransfer.huNumber}
              </Typography>
              <Typography color="text.secondary">
                {selectedTransfer.sourcePointCode}
                {" → "}{selectedTransfer.destinationPointCode}
              </Typography>
              <Alert severity={selectedTransfer.status === "EXCEPTION"
                ? "error" : "info"}>
                {selectedTransfer.status === "OPEN"
                  ? "Confirmez la prise en charge de la HU."
                  : selectedTransfer.status === "IN_PROGRESS"
                    ? t("putaway.transfer.arrivalHelp")
                    : `${selectedTransfer.exceptionCode?.replaceAll("_", " ")}
                      — ${selectedTransfer.exceptionComment}`}
              </Alert>
              {transferExceptionMode === "report" && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="transfer-exception-code-label">
                      Motif
                    </InputLabel>
                    <Select labelId="transfer-exception-code-label"
                      label={t("putaway.fields.reason")} value={transferExceptionCode}
                      onChange={(event) =>
                        setTransferExceptionCode(event.target.value)}>
                      <MenuItem value="HU_MISSING">{t("putaway.transfer.huMissing")}</MenuItem>
                      <MenuItem value="HU_DAMAGED">{t("putaway.transfer.huDamaged")}</MenuItem>
                      <MenuItem value="WRONG_SCAN">{t("putaway.transfer.wrongScan")}</MenuItem>
                      <MenuItem value="SOURCE_INACCESSIBLE">
                        Gare source inaccessible
                      </MenuItem>
                      <MenuItem value="DESTINATION_INACCESSIBLE">
                        Gare destination inaccessible
                      </MenuItem>
                      <MenuItem value="OTHER">{t("putaway.transfer.other")}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label={t("putaway.fields.comment")} multiline minRows={3}
                    value={transferExceptionComment}
                    onChange={(event) =>
                      setTransferExceptionComment(event.target.value)} />
                </>
              )}
              {transferExceptionMode === "resolve" && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="transfer-resolution-label">
                      {t("putaway.fields.resolution")}
                    </InputLabel>
                    <Select labelId="transfer-resolution-label"
                      label={t("putaway.fields.resolution")} value={transferExceptionAction}
                      onChange={(event) =>
                        setTransferExceptionAction(event.target.value)}>
                      <MenuItem value="RETRY">{t("putaway.transfer.retry")}</MenuItem>
                      <MenuItem value="CHANGE_DESTINATION">
                        {t("putaway.transfer.changeDestination")}
                      </MenuItem>
                      <MenuItem value="SEND_TO_QUALITY">
                        {t("putaway.transfer.sendQuality")}
                      </MenuItem>
                      <MenuItem value="CANCEL">{t("putaway.transfer.cancelFlow")}</MenuItem>
                    </Select>
                  </FormControl>
                  {transferExceptionAction === "CHANGE_DESTINATION" && (
                    <FormControl fullWidth>
                      <InputLabel id="transfer-destination-label">
                        {t("putaway.fields.newStation")}
                      </InputLabel>
                      <Select labelId="transfer-destination-label"
                        label={t("putaway.fields.newStation")} value={transferDestinationId}
                        onChange={(event) =>
                          setTransferDestinationId(event.target.value)}>
                        {processPoints.data?.filter((point) =>
                          point.status === "ACTIVE"
                          && point.warehouseCode
                            === selectedTransfer.warehouseCode
                          && point.code !== selectedTransfer.sourcePointCode
                        ).map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <TextField label={t("putaway.fields.resolutionComment")}
                    multiline minRows={3} value={transferExceptionComment}
                    onChange={(event) =>
                      setTransferExceptionComment(event.target.value)} />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSelectedTransfer(null);
            setTransferExceptionMode(null);
          }}>{t("common.actions.close")}</Button>
          {transferExceptionMode === "report" ? (
            <Button variant="contained" color="error"
              disabled={transferExceptionComment.trim().length < 3
                || reportTransferException.isPending}
              onClick={() => reportTransferException.mutate()}>
              {t("common.actions.save")}
            </Button>
          ) : transferExceptionMode === "resolve" ? (
            <Button variant="contained"
              disabled={transferExceptionComment.trim().length < 3
                || (transferExceptionAction === "CHANGE_DESTINATION"
                  && !transferDestinationId)
                || resolveTransferException.isPending}
              onClick={() => resolveTransferException.mutate()}>
              {t("putaway.actions.resolve")}
            </Button>
          ) : (
            <>
              {selectedTransfer?.status === "IN_PROGRESS" && (
                <Button color="error" variant="outlined"
                  onClick={() => {
                    setTransferExceptionMode("report");
                    setTransferExceptionCode("OTHER");
                    setTransferExceptionComment("");
                  }}>
                  {t("putaway.actions.reportProblem")}
                </Button>
              )}
              {selectedTransfer?.status !== "EXCEPTION" && (
                <Button variant="contained" disabled={transferAction.isPending}
                  onClick={() => transferAction.mutate(selectedTransfer!, {
                    onSuccess: () => setSelectedTransfer(null)
                  })}>
                  {selectedTransfer?.status === "OPEN"
                    ? t("putaway.actions.claim") : t("putaway.actions.confirmArrival")}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={packagingAlert !== null}
        onClose={() => setPackagingAlert(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("putaway.actions.identifyPackaging")} · {packagingAlert?.huNumber}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="packaging-identification-label">
                {t("putaway.fields.packaging")}
              </InputLabel>
              <Select labelId="packaging-identification-label"
                label={t("putaway.fields.packaging")} value={packagingId}
                onChange={(event) => setPackagingId(event.target.value)}>
                {packagingOptions.data
                  ?.filter((option) =>
                    option.status === "ACTIVE" && option.code !== "UNKNOWN")
                  .map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.code} · {option.displayName}
                      {" · "}{option.baseQuantity} {option.uom}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField label={t("putaway.fields.comment")} multiline minRows={3}
              value={packagingComment}
              onChange={(event) => setPackagingComment(event.target.value)}
              helperText={t("putaway.help.requiredThree")} />
            {identifyPackaging.isError && (
              <Alert severity="error">
                {identifyPackaging.error instanceof Error
                  ? identifyPackaging.error.message
                  : t("putaway.errors.packaging")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackagingAlert(null)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained"
            disabled={!packagingId || packagingComment.trim().length < 3
              || identifyPackaging.isPending}
            onClick={() => identifyPackaging.mutate()}>
            {t("putaway.actions.resimulate")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resimulationAlert !== null}
        onClose={() => setResimulationAlert(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Resimuler · {resimulationAlert?.huNumber}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              {t("putaway.labels.exclusionsRecorded")}
            </Typography>
            {resimulationAlert?.rejectedBinCode && (
              <FormControlLabel control={
                <Checkbox checked={releaseRejectedBin}
                  onChange={(event) =>
                    setReleaseRejectedBin(event.target.checked)} />
              } label={t("putaway.labels.reauthorizeRejected", { location: resimulationAlert.rejectedBinCode })} />
            )}
            <TextField label={t("putaway.fields.comment")} multiline minRows={3}
              value={resimulationComment}
              onChange={(event) => setResimulationComment(event.target.value)}
              helperText={t("putaway.help.auditedDecision")} />
            {resimulateAlert.isError && (
              <Alert severity="error">
                {resimulateAlert.error instanceof Error
                  ? resimulateAlert.error.message
                  : t("putaway.errors.resimulation")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResimulationAlert(null)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained"
            disabled={resimulationComment.trim().length < 3
              || resimulateAlert.isPending}
            onClick={() => resimulateAlert.mutate()}>
            Resimuler
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
